import React from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import {
  SlotRenderer,
  BackgroundMediaRenderer,
  EmptyMediaSlotAffordance,
  useHeroAnimation,
  useHeroAmbient,
  useHeroVerticalPadding,
} from "./heroModeUtils";
import "./heroModeAnimations.css";
import "./heroSplit.css";

const BP_CONFIG = {
  // ``mediaHoverPct`` is the *expanded* width of the media column
  // for the visitor-driven ``split_hover_expand`` sibling: the
  // resting layout uses ``mediaPct`` (50% / 45%) like every other
  // Split variant, and the CSS rule in ``heroSplit.css`` swaps
  // between the two via a pair of CSS variables on ``:hover`` /
  // ``:focus-within``. Hover values are tuned per breakpoint so
  // the text column always keeps a usable minimum width even at
  // peak expansion (the column relies on ``flex-1 min-w-0`` to
  // wrap text into whatever space is left). Mobile layouts skip
  // the hover affordance entirely (``horizontal: false``), so we
  // don't need ``mediaHoverPct`` there.
  extra_large: { horizontal: true, mediaPct: "50%", mediaHoverPct: "70%", contentPx: 64 },
  large:       { horizontal: true, mediaPct: "50%", mediaHoverPct: "70%", contentPx: 56 },
  medium:      { horizontal: true, mediaPct: "45%", mediaHoverPct: "65%", contentPx: 40 },
  small:       { horizontal: false, mediaMaxH: "44vh", contentPx: 24, contentPy: 32 },
  extra_small: { horizontal: false, mediaMaxH: "38vh", contentPx: 16, contentPy: 24 },
};

// ``split``, ``split_right`` and their soft-edge siblings
// (``split_fade``, ``split_fade_right``) share this renderer; only
// the side the media occupies and whether the inner edge is
// feathered differ. We branch once on mode id and use the result to
// flip:
//   - desktop:  ``flex-row`` → ``flex-row-reverse`` (media on right)
//   - mobile:   ``flex-col`` → ``flex-col-reverse`` (media below text,
//               so the text-first reading order is preserved)
function isMediaRightAnchored(modeId) {
  if (!modeId) return false;
  return (
    modeId === "split_right" ||
    modeId === "split_right_animated" ||
    modeId === "split_fade_right"
  );
}

// Feathered-edge variants paint a CSS mask gradient on the media
// wrapper so the photo dissolves into the user's chosen background
// (color / pattern / image) instead of presenting a hard rectangular
// seam at the half-line. Identifying this from ``item.hero_mode``
// (rather than a separate model field) keeps the visual story owned
// by the mode picker — switching between "Split" and "Split (Soft
// Edge)" is the only knob the user needs.
function isMediaFaded(modeId) {
  if (!modeId) return false;
  return modeId === "split_fade" || modeId === "split_fade_right";
}

// Visitor-driven sibling of ``split`` — the media column starts
// at the canonical rest width (``mediaPct``) and stretches to
// ``mediaHoverPct`` when the visitor hovers / focuses it, with the
// text column compressing into the remaining width via
// ``flex-1 min-w-0``. Identifying this from ``item.hero_mode``
// (rather than a separate model field) keeps the visual story
// owned by the mode picker — switching between "Split" and "Split
// (Hover Expand)" is the only knob the user needs.
//
// The list shape (rather than a single equality check) is
// intentional: when we ship a ``split_hover_expand_right`` spatial
// mirror the renderer needs to recognise both ids without a
// regression. Adding the mirror here later is a one-liner.
function isHoverExpand(modeId) {
  if (!modeId) return false;
  return modeId === "split_hover_expand";
}

// Build the ``mask-image`` gradient that feathers the media's INNER
// edge — i.e. the side that touches the text column. The opposite
// (outer) edge stays fully opaque so the photo still anchors against
// the hero boundary; only the seam toward the text dissolves.
//
// ``solidStop`` is the share of the media that stays at full
// opacity before the gradient kicks in. We use a generous default on
// desktop (the media half is wide enough that a 60% solid + 40%
// fade still shows the photo's focal subject clearly) and a higher
// value on mobile (the media block is shorter, so eating 40% of it
// with a fade would obscure too much of the image).
//
// Returns ``null`` when no fade is needed so callers can spread the
// result without conditionally constructing the style object.
function buildMediaFadeStyle({ faded, mediaOnRight, isHorizontal }) {
  if (!faded) return null;

  let direction;
  let solidStop;
  if (isHorizontal) {
    // Desktop: feather the side opposite the outer edge.
    //   media on LEFT  → fade toward the RIGHT (text column).
    //   media on RIGHT → fade toward the LEFT  (text column).
    direction = mediaOnRight ? "to left" : "to right";
    solidStop = "60%";
  } else {
    // Mobile: media stacks above OR below the text column.
    //   ``mediaOnRight`` (split_fade_right) uses ``flex-col-reverse`` →
    //     media is BELOW text → inner edge is the TOP → fade upward.
    //   default (split_fade) uses ``flex-col`` → media is ABOVE text
    //     → inner edge is the BOTTOM → fade downward.
    direction = mediaOnRight ? "to top" : "to bottom";
    solidStop = "70%";
  }

  const gradient = `linear-gradient(${direction}, black 0%, black ${solidStop}, transparent 100%)`;
  return {
    // ``-webkit-`` prefix is still required on Safari (desktop +
    // iOS) — the unprefixed property became standard but Safari
    // shipped the prefixed form first and the prefixed form is what
    // currently works without a flag.
    WebkitMaskImage: gradient,
    maskImage: gradient,
  };
}

const HeroSplit = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const mediaSlotData = heroSlots?.getSlot("media");
  const hasMedia = !!mediaSlotData;

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;
  const isHorizontal = config.horizontal && hasMedia;
  const mediaOnRight = isMediaRightAnchored(item?.hero_mode);
  // Soft-edge mode? We feather the media's inner edge so the photo
  // dissolves into the hero background. The mask is applied to the
  // media WRAPPER (not BackgroundMediaRenderer directly) because the
  // wrapper's box defines the half — masking it ensures both the
  // ``<img>``-style div and the ``<video>`` variants of the media
  // are clipped uniformly without each renderer needing fade-aware
  // logic of its own.
  const faded = isMediaFaded(item?.hero_mode);
  const mediaFadeStyle = buildMediaFadeStyle({
    faded,
    mediaOnRight,
    isHorizontal,
  });
  // Hover-expand mode? On desktop (``isHorizontal``) we hand the
  // media column's width over to the CSS rule in ``heroSplit.css``
  // by mounting the ``hero-split-media--hover-expand`` modifier
  // and exposing the rest / hover widths as CSS custom properties.
  // The CSS rule reads those vars at rest and on ``:hover`` /
  // ``:focus-within`` and animates between them; doing the swap in
  // CSS (rather than React state) keeps the transition smooth and
  // avoids a render every time the visitor moves the pointer in
  // or out. On mobile the layout stacks vertically so the hover
  // affordance has no meaning — we silently fall through to the
  // canonical resting width and skip the modifier.
  const hoverExpand = isHoverExpand(item?.hero_mode) && isHorizontal;

  // Vertical break-out for the media column. Without this, the image
  // sits inside the hero root's padding box and shows a band of
  // background above and below — see ``useHeroVerticalPadding`` for
  // the full reasoning.
  //
  // DESKTOP (``isHorizontal``): both top and bottom margins are
  // negated so the media column stretches to the hero root's outer
  // padding-box edges without disturbing the flex row's height.
  //
  // MOBILE (stacked): the media breaks out of the padding on the
  // side that touches the hero edge — top for the default
  // ``flex-col`` layout (media on top) and bottom for
  // ``flex-col-reverse`` (media on bottom). This ensures the
  // image always anchors flush against the nearest hero edge
  // while the text column retains its own padding.
  const heroPad = useHeroVerticalPadding(heroBase.heroRootRef);
  const mediaBreakoutStyle = isHorizontal
    ? {
        marginTop: heroPad.top ? `-${heroPad.top}px` : 0,
        marginBottom: heroPad.bottom ? `-${heroPad.bottom}px` : 0,
      }
    : mediaOnRight
      ? { marginBottom: heroPad.bottom ? `-${heroPad.bottom}px` : 0 }
      : { marginTop: heroPad.top ? `-${heroPad.top}px` : 0 };

  const entrance = heroSlots?.animatedEntrance || "fade-up";
  // Split's ambient animation (ken-burns, shipped with
  // ``split_animated``) attaches to the media half only — the text
  // column stays still so the ambient motion doesn't fight the text's
  // entrance animation. Ambient is mode-owned, independent of the
  // entrance toggle.
  const mediaAmbientClass = useHeroAmbient(heroSlots?.ambientAnimation);

  const contentAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });

  if (!hasMedia) {
    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        <Background
          bgImage={heroBase.bgImage}
          imageStyleClasses={heroBase.imageStyleClasses}
          imageInlineStyles={heroBase.imageInlineStyles}
          colorClass={heroBase.colorClass}
          colorStyle={heroBase.colorStyle}
          jsPatternLayer={heroBase.jsPatternLayer}
        />
        <ContainerEffectOverlay data={heroBase.data} />
        <div
          className={
            responsiveHeight
              ? "relative z-10 flex items-center justify-center w-full"
              : "absolute inset-0 z-10 flex items-center justify-center"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          <div
            ref={contentAnim.ref}
            className={`flex flex-col gap-4 w-full max-w-2xl mx-auto ${contentAnim.className}`}
            style={{ padding: config.contentPx, ...contentAnim.style, ...contentInsetStyle }}
          >
            {/* Edit-mode "+ Add Image" affordance for the empty media
                slot — same one Profile shows for its empty portrait,
                so every image-bearing mode has a one-click way to
                populate its image slot. Renders ``null`` outside the
                editor. */}
            <EmptyMediaSlotAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="media"
              order={0}
            />
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      {/* Hero background stays *behind* the split layout so whatever the
          user configured in the Style panel (color / pattern / image) is
          visible in the non-media half. Without this layer, picking Split
          mode erased the user's background — the media column covers its
          own half, but the content column had nothing painted behind it.
          The media renderer below sits on top of this and opaquely covers
          its half, so there's no visible bleed-through. */}
      <Background
        bgImage={heroBase.bgImage}
        imageStyleClasses={heroBase.imageStyleClasses}
        imageInlineStyles={heroBase.imageInlineStyles}
        colorClass={heroBase.colorClass}
        colorStyle={heroBase.colorStyle}
        jsPatternLayer={heroBase.jsPatternLayer}
      />
      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={`${
          isHorizontal
            ? mediaOnRight ? "flex flex-row-reverse" : "flex flex-row"
            : mediaOnRight ? "flex flex-col-reverse" : "flex flex-col"
        } w-full ${
          responsiveHeight ? "relative z-10" : "absolute inset-0 z-10"
        }`}
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        {/* Media column.
            ===================================================================
            Sizing rules differ between desktop (``isHorizontal``) and
            mobile because the failure modes are opposite.

            DESKTOP (``isHorizontal: true``)
            --------------------------------
            Critical: do NOT set ``height: '100%'``. The parent flex
            row uses ``minHeight`` (the ``responsive_height`` mode —
            the default for new heros) rather than a fixed ``height``;
            CSS percentage heights only resolve against a parent with
            a *definite* height, so ``height: 100%`` here computes to
            ``0`` (or ``auto``) in most browsers. When it computes to
            ``0`` the explicit value wins over flexbox's
            ``align-items: stretch`` default and the column collapses,
            leaving ``BackgroundMediaRenderer`` (``position: absolute;
            inset: 0``) with nothing to fill. Letting
            ``align-items: stretch`` (default) own the cross-axis
            sizing makes the column fill the row's resolved height.

            MOBILE (``isHorizontal: false``)
            --------------------------------
            On the stacked mobile layout the column is now a *main-
            axis* item (the parent is ``flex-col``), so
            ``align-items: stretch`` no longer applies — it controls
            the cross axis (width), not height. The column's intrinsic
            height comes from its children, but its only meaningful
            child is ``BackgroundMediaRenderer`` (``position:
            absolute``), which contributes nothing to layout flow.
            Without an explicit ``minHeight`` the column collapses to
            ``0`` and the image disappears. We previously tried to
            enforce height via an ``absolute inset-0`` sibling div
            with ``minHeight``, but absolute-positioned elements never
            push their parent's box, so that hack was a no-op — that
            was the user-reported "image doesn't show in mobile
            Split/Magazine" bug. The fix is to put ``minHeight``
            directly on the column itself, paired with ``maxHeight``
            so the media half doesn't dominate small screens. */}
        <div
          data-hero-split-media={faded ? "faded" : "solid"}
          // ``hero-split-media--hover-expand`` (mounted only on the
          // desktop branch via ``hoverExpand``) is the CSS hook the
          // ``heroSplit.css`` rule keys on for the visitor-driven
          // width swap. The ``min-w-0`` is critical: without it the
          // wrapper's intrinsic width wins over the CSS variable
          // we set below and the column refuses to compress when
          // the visitor stops hovering. ``flex-shrink-0`` is kept
          // for the static / fade siblings (they pin a fixed
          // ``width`` percentage and never need to flex) but we
          // drop it for the hover-expand variant so the column can
          // freely move between rest and hover widths under the
          // CSS transition.
          className={`relative overflow-hidden ${
            hoverExpand
              ? "min-w-0 hero-split-media--hover-expand"
              : "flex-shrink-0"
          }`}
          // ``tabIndex={0}`` is the keyboard equivalent of the
          // mouse hover affordance — without it, keyboard / touch
          // users can't trigger ``:focus-within`` and the column
          // never expands for them. We add it ONLY for the
          // hover-expand variant; every other Split sibling has a
          // decorative media block with no interactive role.
          tabIndex={hoverExpand ? 0 : undefined}
          style={{
            ...(isHorizontal
              ? hoverExpand
                ? {
                    // Hand width control over to the CSS rule via
                    // custom properties — the rule applies
                    // ``--hero-split-media-rest-width`` at rest
                    // and swaps to ``--hero-split-media-hover-width``
                    // under ``:hover`` / ``:focus-within`` with a
                    // smooth transition. Don't set ``width``
                    // inline here — it would pin the column to a
                    // single value and the CSS swap would never
                    // fire.
                    "--hero-split-media-rest-width": config.mediaPct,
                    "--hero-split-media-hover-width": config.mediaHoverPct,
                  }
                : { width: config.mediaPct }
              : {
                  width: "100%",
                  minHeight: config.mediaMaxH,
                  maxHeight: config.mediaMaxH,
                }),
            // Vertical break-out (desktop only) — pulls the column
            // past the hero root's padding so the image bleeds to
            // the top and bottom hero edges. ``null`` on mobile.
            ...(mediaBreakoutStyle || {}),
            // Spread mediaFadeStyle LAST so the mask gradient wins
            // over any earlier sizing-only rules. ``buildMediaFadeStyle``
            // returns ``null`` for non-fade modes, so the spread is a
            // no-op in those cases.
            ...(mediaFadeStyle || {}),
          }}
        >
          <BackgroundMediaRenderer
            slotData={mediaSlotData}
            heroBase={heroBase}
            ambientClass={mediaAmbientClass}
            heroItem={item}
          />
        </div>

        <div
          ref={contentAnim.ref}
          className={`flex flex-col justify-center gap-4 flex-1 min-w-0 ${contentAnim.className}`}
          style={{
            ...(isHorizontal
              ? { paddingLeft: config.contentPx, paddingRight: config.contentPx }
              : { paddingLeft: config.contentPx, paddingRight: config.contentPx, paddingTop: config.contentPy, paddingBottom: config.contentPy }),
            ...contentAnim.style,
          }}
        >
          {/* ``min-w-0`` is the critical bit: flex items default to
              ``min-width: auto``, which pins the column to the
              intrinsic width of its widest child. When that child is a
              ButtonCollection grid with ``max-content`` tracks (or a
              long-word title), the column refuses to shrink and the
              text wraps to fit *that* bloated width — producing the
              "lines extend past the visible hero" symptom. With
              ``min-width: 0`` the column honours its ``flex-1``
              allocation and text inside wraps to fit the *visible*
              width instead. */}
          <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} />
          <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} />
          <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} />
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroSplit);
